import { useState } from 'react'
import { X, FileText, Share2, User, Users } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { TeamMemberSelector, useTeamMemberSelection } from '@/teams'

interface ShareCandidateModalProps {
  open: boolean
  onClose: () => void
  candidate: any
  onShared?: () => void
}

export default function ShareCandidateModal({ open, onClose, candidate, onShared }: ShareCandidateModalProps) {
  const [shareType, setShareType] = useState<'team' | 'individual'>('team')
  const [permissionLevel, setPermissionLevel] = useState<'view' | 'edit' | 'interview' | 'transfer'>('view')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  
  const {
    selectedTeamId,
    selectedUserId,
    setSelectedTeamId,
    setSelectedUserId,
    resetSelection
  } = useTeamMemberSelection()

  const resetForm = () => {
    setShareType('team')
    setPermissionLevel('view')
    setNotes('')
    resetSelection()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!candidate?.id) return

    try {
      setLoading(true)
      
      const response = await apiClient.shareCandidate(candidate.id, {
        targetUserId: shareType === 'individual' ? selectedUserId || undefined : undefined,
        targetTeamId: shareType === 'team' ? selectedTeamId || undefined : undefined,
        permissionLevel: permissionLevel,
        shareReason: 'Shared via interface',
        notes: notes
      })

      if (response?.success) {
        resetForm()
        onShared?.()
        onClose()
      } else {
        throw new Error(response?.error || 'Failed to share candidate')
      }
    } catch (err) {
      console.error('Failed to share candidate:', err)
      alert('Failed to share candidate: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Share2 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Share Candidate</h2>
              <p className="text-sm text-gray-500">{candidate?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Share Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Share with
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="team"
                    checked={shareType === 'team'}
                    onChange={(e) => setShareType(e.target.value as 'team' | 'individual')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    Entire Team
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="individual"
                    checked={shareType === 'individual'}
                    onChange={(e) => setShareType(e.target.value as 'team' | 'individual')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700 flex items-center">
                    <User className="w-4 h-4 mr-1" />
                    Individual Member
                  </span>
                </label>
              </div>
            </div>

            {/* Team/Member Selection */}
            <TeamMemberSelector
              selectedTeamId={selectedTeamId}
              selectedUserId={selectedUserId}
              onTeamChange={setSelectedTeamId}
              onUserChange={setSelectedUserId}
              selectionMode={shareType === 'team' ? 'team' : 'both'}
              required={true}
              label={shareType === 'team' ? 'Select Team' : 'Select Team and Member'}
              placeholder="Choose a team..."
            />

            {/* Permission Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permission Level
              </label>
              <select
                value={permissionLevel}
                onChange={(e) => setPermissionLevel(e.target.value as 'view' | 'edit' | 'interview' | 'transfer')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="view">View - Can view candidate details</option>
                <option value="edit">Edit - Can view and edit candidate information</option>
                <option value="interview">Interview - Can view, edit, and conduct interviews</option>
                <option value="transfer">Transfer - Full ownership transfer</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <div className="relative">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note about why you're sharing this candidate..."
                  rows={3}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <FileText className="w-5 h-5 text-gray-400 absolute left-3 top-3" />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !selectedTeamId || (shareType === 'individual' && !selectedUserId)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Share2 className="w-4 h-4" />
                    <span>
                      Share with {shareType === 'team' ? 'Team' : 'Individual'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}